import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, SyntheticEvent } from "react";

interface HoldOptions {
  /** How long a press must last before it counts as a hold. */
  delay?: number;
  /** Pointer drift (px) that cancels a pending hold, so a scroll is never a hold. */
  moveTolerance?: number;
}

/**
 * Press-and-hold that does not eat ordinary taps.
 *
 * Three rules make that true:
 *  1. 450ms threshold — a deliberate press, not a slightly slow tap.
 *  2. The hold is cancelled as soon as the pointer drifts, so dragging or
 *     scrolling over the element never arms it.
 *  3. `didHold` is cleared SYNCHRONOUSLY inside the click handler that follows.
 *     Clearing it on a `setTimeout(..., 0)` races React's click dispatch, and
 *     losing that race is exactly what swallows a tap.
 *
 * The held payload is generic so a list can hold the id of the pressed row.
 */
export function useHoldReveal<T>({ delay = 450, moveTolerance = 10 }: HoldOptions = {}) {
  const [held, setHeld] = useState<T | null>(null);
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const didHold = useRef(false);

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => () => clear(), [clear]);

  const start = useCallback(
    (value: T) => {
      clear();
      didHold.current = false;
      timer.current = window.setTimeout(() => {
        timer.current = null;
        didHold.current = true;
        setHeld(value);
        navigator.vibrate?.(12);
      }, delay);
    },
    [clear, delay]
  );

  const end = useCallback(() => {
    clear();
    origin.current = null;
    setHeld(null);
  }, [clear]);

  /** Spread onto the pressable element. `value` is what `held` becomes. */
  const handlers = (value: T) => ({
    onPointerDown(event: ReactPointerEvent) {
      if (event.button > 0) return;
      origin.current = { x: event.clientX, y: event.clientY };
      start(value);
    },
    onPointerMove(event: ReactPointerEvent) {
      if (timer.current === null || !origin.current) return;
      const drift = Math.hypot(event.clientX - origin.current.x, event.clientY - origin.current.y);
      if (drift > moveTolerance) clear();
    },
    onPointerUp: end,
    onPointerCancel: end,
    onPointerLeave: end,
    onContextMenu(event: SyntheticEvent) {
      event.preventDefault();
    }
  });

  /** Wrap a click handler so a completed hold consumes exactly one click. */
  const wrapClick = (run: () => void) => () => {
    if (didHold.current) {
      didHold.current = false;
      return;
    }
    run();
  };

  return { held, start, end, clear, handlers, wrapClick };
}
